import { formatDurationMinutes, formatTime } from "@/hooks/use-formatter.hook.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiTimesheetDay } from "@/types/time-tracking.types.ts";
import {
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { AdjustTimeModal } from "../widgets/AdjustTimeModal.tsx";

export const TimesheetsTableCell = ({
  day,
  employee,
  canEdit,
}: {
  day: ApiTimesheetDay;
  employee: ApiEmployee;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const hasEntries = day.entries.length > 0;

  const openAdjust = () => {
    openModal(AdjustTimeModal, {
      employeeId: employee.id,
      date: day.date_day,
      canEdit,
    });
  };

  const cellContent = (
    <Text className={`tabular-nums ${day.has_active ? "text-positive" : ""}`}>
      {formatDurationMinutes(day.duration_minutes)}
    </Text>
  );

  if (!hasEntries) {
    if (!canEdit) return cellContent;
    return (
      <button onClick={openAdjust} className={"text-left cursor-pointer hover:underline"}>
        {cellContent}
      </button>
    );
  }

  const tooltipBody = (
    <Stack gap={"xs"}>
      {day.entries.map((entry) => (
        <Stack key={entry.id} gap={"xs"}>
          <Text size={"xs"}>
            {formatTime(entry.datetime_clocked_in) || "–"} –{" "}
            {formatTime(entry.datetime_clocked_out) || "–"}
            {entry.is_active && ` (${t("clocking:active")})`}
          </Text>
          {(entry.clock_in_comment || entry.clock_out_comment) && (
            <Text size={"xs"} className={"text-secondary"}>
              {entry.clock_in_comment ?? entry.clock_out_comment}
            </Text>
          )}
        </Stack>
      ))}
      {canEdit && (
        <Text size={"xs"} className={"text-brand mt-1"}>
          {t("clocking:click_to_adjust")}
        </Text>
      )}
    </Stack>
  );

  return (
    <Tooltip delay={false}>
      <TooltipTrigger>
        <button onClick={openAdjust} className={"text-left cursor-pointer hover:underline"}>
          {cellContent}
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltipBody}</TooltipContent>
    </Tooltip>
  );
};
